import { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { ArrowRight, Phone, Loader2, MapPin, Calendar, CheckSquare, Image as ImageIcon } from "lucide-react";
import { companyInfo } from "../data";
import ImageModal from "../components/ImageModal";
import useMetadata from "../hooks/useMetadata";

export default function ProjectDetail() {
  const { projektSlug } = useParams();
  const navigate = useNavigate();
  const [project, setProject] = useState(null);
  const [relatedProjects, setRelatedProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeImageIndex, setActiveImageIndex] = useState(null);

  useMetadata(
    project ? project.titel : "Projekt",
    project ? project.beskrivelse : "Se detaljer om vores udførte jord-, grave- og anlægsarbejde."
  );

  useEffect(() => {
    async function fetchProjectData() {
      setLoading(true);
      try {
        // 1. Fetch project by slug field
        const projectRes = await fetch(
          `https://api.dev.auxo.dk/public/v2/content/projekter?projekt_slug=${projektSlug}`
        );
        const projectData = await projectRes.json();

        if (!projectData.items || projectData.items.length === 0) {
          // If project not found, redirect to 404
          navigate("/404", { replace: true });
          return;
        }

        const currentProject = projectData.items[0];
        setProject(currentProject);

        // 2. Fetch all projects to show related (excluding current)
        const allProjectsRes = await fetch(
          "https://api.dev.auxo.dk/public/v2/content/projekter"
        );
        const allProjectsData = await allProjectsRes.json();
        if (allProjectsData.items) {
          const filtered = allProjectsData.items.filter(
            (p) => p.id !== currentProject.id
          );
          setRelatedProjects(filtered);
        }
      } catch (error) {
        console.error("Fejl ved hentning af projektdetaljer:", error);
      } finally {
        setLoading(false);
      }
    }

    fetchProjectData();
  }, [projektSlug, navigate]);

  if (loading) {
    return (
      <div className="flex min-h-[60vh] items-center justify-center bg-brand-dark text-white">
        <div className="text-center space-y-3">
          <Loader2 className="mx-auto h-10 w-10 animate-spin text-brand-accent" />
          <p className="text-sm text-gray-400">Henter projektoplysninger...</p>
        </div>
      </div>
    );
  }

  if (!project) return null;

  // Split the comma-separated string of services done
  const servicesDone = project.udfoerte_ydelser.da
    .split(",")
    .map((s) => s.trim());

  return (
    <div className="relative bg-brand-dark text-white">
      {/* Main Content Section */}
      <section className="py-16 sm:py-24 bg-white text-brand-charcoal">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          {/* Project Title & Metadata */}
          <div className="mb-12 border-b border-gray-100 pb-8">
            <h1 className="font-display text-3xl font-extrabold tracking-tight sm:text-4xl md:text-5xl lg:text-5xl uppercase text-brand-charcoal">
              {project.projekttitel.da}
            </h1>
            <div className="mt-4 flex flex-wrap items-center justify-start gap-6 text-sm text-gray-500">
              <span className="flex items-center gap-1.5">
                <MapPin className="h-4 w-4 text-brand-accent" />
                {project.lokation.da}
              </span>
              <span className="flex items-center gap-1.5">
                <Calendar className="h-4 w-4 text-brand-accent" />
                Udført i 2026
              </span>
            </div>
          </div>

          <div className="grid grid-cols-1 gap-12 lg:grid-cols-12">
            {/* Left Column: Description */}
            <div className="lg:col-span-7 space-y-6">
              <h2 className="font-display text-2xl font-bold text-brand-charcoal sm:text-3xl uppercase">
                Projektbeskrivelse
              </h2>
              <div
                className="prose max-w-none text-gray-600 text-sm sm:text-base leading-relaxed space-y-4"
                dangerouslySetInnerHTML={{ __html: project.beskrivelse.da }}
              />
            </div>

            {/* Right Column: Tasks Done */}
            <div className="lg:col-span-5">
              <div className="rounded-none border border-brand-gray-dark bg-brand-charcoal p-6 sm:p-8 shadow-sm text-white">
                <h3 className="text-lg font-bold text-white mb-6 uppercase">
                  Udførte anlægsopgaver
                </h3>
                <ul className="space-y-4">
                  {servicesDone.map((service, index) => (
                    <li key={index} className="flex items-start gap-3">
                      <CheckSquare className="h-5 w-5 text-brand-accent shrink-0 mt-0.5" />
                      <span className="text-sm text-gray-200 font-semibold">
                        {service}
                      </span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Project Gallery Section */}
      {project.galleri && project.galleri.length > 0 && (
        <section className="py-16 sm:py-24 border-t border-gray-200 bg-gray-50">
          <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
            <div className="text-center max-w-3xl mx-auto mb-12">
              <h2 className="font-display text-2xl font-bold tracking-tight text-brand-charcoal sm:text-3xl mt-2 uppercase">
                Billeder fra byggepladsen
              </h2>
            </div>

            <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
              {project.galleri.map((imgUrl, index) => (
                <div
                  key={index}
                  onClick={() => setActiveImageIndex(index)}
                  className="group relative h-64 overflow-hidden rounded-none border border-gray-200 bg-white shadow-sm cursor-pointer"
                >
                  <img
                    src={`https://assets.auxodev.dk/cdn-cgi/image/width=500,height=380,fit=cover,quality=80,format=auto/${imgUrl.replace(
                      "https://assets.auxodev.dk/",
                      ""
                    )}`}
                    alt={`${project.projekttitel.da} galleri ${index + 1}`}
                    className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
                  />
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Contact CTA */}
      <section className="border-t border-brand-gray-dark bg-brand-charcoal/40 py-20 text-center">
        <div className="mx-auto max-w-4xl px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-2xl font-bold text-white sm:text-3xl uppercase">
            Skal vi løse dit næste anlægsprojekt?
          </h2>
          <p className="mt-3 text-gray-300 text-sm sm:text-base max-w-2xl mx-auto leading-relaxed">
            Kontakt os i dag for en uforpligtende snak om dit projekt. Vi rådgiver dig gerne om den bedste løsning og udarbejder et uforpligtende tilbud.
          </p>
          <div className="mt-8 flex flex-col sm:flex-row items-center justify-center gap-4">
            <Link
              to="/kontakt"
              className="w-full sm:w-auto inline-flex items-center justify-center gap-2 rounded bg-brand-accent px-6 py-3 text-xs font-bold uppercase tracking-wider text-white transition-colors hover:bg-brand-accent-hover"
            >
              Få et tilbud
              <ArrowRight className="h-4 w-4" />
            </Link>
            <a
              href={`tel:${companyInfo.phone}`}
              className="w-full sm:w-auto inline-flex items-center justify-center gap-2 rounded border border-white bg-transparent px-6 py-3 text-xs font-bold uppercase tracking-wider text-white transition-colors hover:bg-white/10 hover:border-white"
            >
              <Phone className="h-4 w-4 text-brand-accent" />
              <span>{companyInfo.phoneFormatted}</span>
            </a>
          </div>
        </div>
      </section>

      {/* Lightbox Modal */}
      {activeImageIndex !== null && (
        <ImageModal
          images={project.galleri}
          initialIndex={activeImageIndex}
          onClose={() => setActiveImageIndex(null)}
        />
      )}
    </div>
  );
}
